import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium font-headline uppercase tracking-widest transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive cursor-pointer",
    {
        variants: {
            variant: {
                default:
                    "performance-gradient text-white shadow-lg shadow-primary/10 hover:opacity-90 active:scale-[0.98]",
                secondary:
                    "bg-surface-container-highest text-on-surface hover:bg-surface-container-high active:bg-surface-variant",
                outlined:
                    "bg-transparent text-primary font-bold hover:bg-surface-container-low active:bg-surface-container",
            },
            size: {
                default: "rounded-md px-4 py-2",
                sm: "rounded-md px-3 py-1.5 text-xs",
                md: "rounded-md px-5 py-2.5",
                lg: "rounded-md px-6 py-4",
                icon: "size-9 rounded-md",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

function Button({
    className,
    variant,
    size,
    asChild = false,
    ...props
}: React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    }) {
    const Comp = asChild ? Slot : "button";

    return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
